import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import ProgressBar from "react-bootstrap/ProgressBar";
import Modal from "react-bootstrap/Modal";
import {
  Upload,
  Sparkles,
  FileText,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Save,
  Eye
} from "lucide-react";
import { generateContentFromPDF, saveGeneratedContent, getMe } from "../../api/client";

export default function AIContentGenerator({ onContentGenerated, t }) {
  const [file, setFile] = useState(null);
  const [yearLevel, setYearLevel] = useState('1');
  const [semester, setSemester] = useState('1');
  const [user, setUser] = useState(null);
  const [subject, setSubject] = useState('Rechnungswesen');

  useEffect(() => {
    getMe().then((me) => {
      if (me) {
        setUser(me);
        // Set smart default: if no RW permission, default to BW
        if (!me.has_rechnungswesen && me.has_betriebswirtschaft) {
          setSubject('Betriebswirtschaft');
        }
      }
    }).catch(() => {});
  }, []);

  // Calculate semester options based on year level (continuous: year 1 = sem 1-2, year 2 = sem 3-4, etc.)
  const getSemesterOptions = (year) => {
    const yearNum = parseInt(year);
    const firstSem = (yearNum - 1) * 2 + 1;
    const secondSem = firstSem + 1;
    return [
      { value: String(firstSem), label: `${firstSem}. Semester` },
      { value: String(secondSem), label: `${secondSem}. Semester` }
    ];
  };

  const handleYearChange = (newYear) => {
    setYearLevel(newYear);
    // Reset semester to first option of new year
    const firstSem = (parseInt(newYear) - 1) * 2 + 1;
    setSemester(String(firstSem));
  };
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(null);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError(t?.contentCreator?.onlyPdfAllowed || 'Nur PDF-Dateien sind erlaubt');
    }
  };

  const handleGenerate = async () => {
    if (!file) {
      setError(t?.contentCreator?.selectPdfFirst || 'Bitte wählen Sie zuerst eine PDF-Datei aus');
      return;
    }

    setGenerating(true);
    setError(null);
    setProgress(10);
    setProgressText(t?.contentCreator?.extractingPdf || 'PDF wird extrahiert...');

    try {
      setProgress(30);
      setProgressText(t?.contentCreator?.analyzingContent || 'Inhalt wird analysiert...');

      const result = await generateContentFromPDF(file, {
        yearLevel,
        semester,
        subject,
        autoSave: false
      });

      setProgress(90);
      setProgressText(t?.contentCreator?.processingResult || 'Ergebnis wird verarbeitet...');

      if (result.ok && result.content) {
        setGeneratedContent(result.content);
        setProgress(100);
        setProgressText(t?.contentCreator?.generationComplete || 'Generierung abgeschlossen!');
        setShowPreview(true);
      } else {
        throw new Error('Invalid response');
      }
    } catch (err) {
      console.error('Generation error:', err);
      let errorMsg = t?.contentCreator?.generationFailed || 'Generierung fehlgeschlagen';
      if (err.message === 'openrouter_api_error') {
        errorMsg = t?.contentCreator?.apiError || 'API-Fehler. Bitte versuchen Sie es später erneut.';
      } else if (err.message === 'pdf_extraction_failed') {
        errorMsg = t?.contentCreator?.pdfExtractionFailed || 'PDF konnte nicht gelesen werden.';
      } else if (err.message === 'ai_response_parse_failed') {
        errorMsg = t?.contentCreator?.parseError || 'AI-Antwort konnte nicht verarbeitet werden.';
      }
      setError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveContent = async () => {
    if (!generatedContent) return;

    setSaving(true);
    setError(null);

    try {
      await saveGeneratedContent(generatedContent);
      setSaveSuccess(true);
      setTimeout(() => {
        setShowPreview(false);
        setGeneratedContent(null);
        setFile(null);
        setSaveSuccess(false);
        if (onContentGenerated) {
          onContentGenerated();
        }
      }, 1500);
    } catch (err) {
      console.error('Save error:', err);
      setError(t?.contentCreator?.saveFailed || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div className="d-flex align-items-center text-white">
            <Sparkles size={24} className="me-2" />
            <div>
              <h5 className="mb-0">{t?.contentCreator?.aiGenerator || 'KI-Inhaltsgenerator'}</h5>
              <small className="opacity-75">
                {t?.contentCreator?.aiGeneratorDesc || 'Laden Sie ein PDF hoch und lassen Sie KI Module und Quiz erstellen'}
              </small>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* Subject */}
          <Form.Group className="mb-3">
            <Form.Label>{t?.contentCreator?.subject || 'Fach'}</Form.Label>
            <Form.Select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!user || !(user?.has_rechnungswesen && user?.has_betriebswirtschaft)}
              style={!(user?.has_rechnungswesen && user?.has_betriebswirtschaft) ? { backgroundColor: 'var(--bg-secondary, #f8f9fa)', cursor: 'not-allowed', opacity: 1 } : {}}
            >
              {(user?.has_rechnungswesen) && (
                <option value="Rechnungswesen">Rechnungswesen</option>
              )}
              {(user?.has_betriebswirtschaft) && (
                <option value="Betriebswirtschaft">Betriebswirtschaft</option>
              )}
            </Form.Select>
            {user && !(user?.has_rechnungswesen && user?.has_betriebswirtschaft) && (
              <Form.Text className="text-muted">
                Du hast nur Zugriff auf dieses Fach.
              </Form.Text>
            )}
          </Form.Group>

          {/* Options Row */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.yearLevel || 'Jahrgang'}</Form.Label>
                <Form.Select
                  value={yearLevel}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  <option value="1">1. Klasse</option>
                  <option value="2">2. Klasse</option>
                  <option value="3">3. Klasse</option>
                  <option value="4">4. Klasse</option>
                  <option value="5">5. Klasse</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.semester || 'Semester'}</Form.Label>
                <Form.Select
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                >
                  {getSemesterOptions(yearLevel).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* File Upload */}
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>{t?.contentCreator?.uploadPdf || 'PDF-Datei hochladen'}</strong>
            </Form.Label>
            <div
              className={`border rounded p-4 text-center ${file ? 'border-success bg-success bg-opacity-10' : 'border-dashed'}`}
              style={{ borderStyle: file ? 'solid' : 'dashed', cursor: 'pointer' }}
              onClick={() => document.getElementById('pdf-upload').click()}
            >
              <input
                type="file"
                id="pdf-upload"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="d-flex align-items-center justify-content-center">
                  <FileText size={24} className="text-success me-2" />
                  <span className="fw-medium">{file.name}</span>
                  <Badge bg="success" className="ms-2">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Badge>
                </div>
              ) : (
                <>
                  <Upload size={32} className="text-muted mb-2" />
                  <p className="mb-0 text-muted">
                    {t?.contentCreator?.clickToUpload || 'Klicken Sie hier oder ziehen Sie eine PDF-Datei hierher'}
                  </p>
                  <small className="text-muted">Max. 20 MB</small>
                </>
              )}
            </div>
          </Form.Group>

          {/* Progress */}
          {generating && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small">{progressText}</span>
                <span className="small">{progress}%</span>
              </div>
              <ProgressBar animated now={progress} variant="primary" />
            </div>
          )}

          {/* Generate Button */}
          <Button
            variant="primary"
            size="lg"
            className="w-100"
            onClick={handleGenerate}
            disabled={generating || !file}
          >
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t?.contentCreator?.generating || 'Generiere...'}
              </>
            ) : (
              <>
                <Sparkles size={18} className="me-2" />
                {t?.contentCreator?.generateContent || 'Inhalte generieren'}
              </>
            )}
          </Button>
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" scrollable>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <Eye size={20} className="me-2" />
            {t?.contentCreator?.previewGenerated || 'Generierte Inhalte'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveSuccess ? (
            <div className="text-center py-5">
              <CheckCircle size={64} className="text-success mb-3" />
              <h4 className="text-success">
                {t?.contentCreator?.savedSuccessfully || 'Erfolgreich gespeichert!'}
              </h4>
            </div>
          ) : generatedContent && (
            <>
              {/* Module Info */}
              <Card className="mb-3 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">
                    <BookOpen size={18} className="me-2" />
                    {generatedContent.module?.title}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p className="mb-1"><strong>ID:</strong> {generatedContent.module?.id}</p>
                      <p className="mb-0"><strong>Beschreibung:</strong> {generatedContent.module?.description}</p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>Klasse:</strong> {generatedContent.module?.year_level}</p>
                      <p className="mb-0"><strong>Semester:</strong> {generatedContent.module?.semester}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Lessons */}
              <h5 className="mb-3">
                {t?.contentCreator?.lessons || 'Lektionen'} ({generatedContent.lessons?.length || 0})
              </h5>
              {generatedContent.lessons?.map((lesson, idx) => (
                <Card key={lesson.id} className="mb-2">
                  <Card.Header className="bg-light py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        <Badge bg="secondary" className="me-2">{idx + 1}</Badge>
                        <strong>{lesson.title}</strong>
                      </span>
                      <div>
                        <Badge bg="info" className="me-1">
                          {lesson.content_sections?.length || 0} {t?.contentCreator?.sections || 'Abschnitte'}
                        </Badge>
                        <Badge bg="success">
                          {lesson.exercises?.length || 0} {t?.contentCreator?.exercises || 'Übungen'}
                        </Badge>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <p className="small text-muted mb-1">{lesson.description}</p>
                    <div className="d-flex flex-wrap gap-1">
                      {lesson.learning_objectives?.slice(0, 3).map((obj, i) => (
                        <Badge key={i} bg="light" text="dark" className="fw-normal">
                          {obj}
                        </Badge>
                      ))}
                      {lesson.learning_objectives?.length > 3 && (
                        <Badge bg="light" text="muted">+{lesson.learning_objectives.length - 3}</Badge>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)} disabled={saving}>
            {t?.common?.cancel || 'Abbrechen'}
          </Button>
          <Button variant="success" onClick={handleSaveContent} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t?.common?.loading || 'Lädt...'}
              </>
            ) : (
              <>
                <Save size={16} className="me-2" />
                {t?.contentCreator?.saveAll || 'Alle speichern'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
